import { css } from '@emotion/core';
import { BrowserUtils } from '../../utils';

const highContrastCheckedStyles = color => `
& + .vds-checkbox-label::after {
  content: '';
  line-height: 1;
  text-align: center;
  color: ${color};
  color: windowText;
  background-image: none !important;
}

&.vds-checkbox:checked + .vds-checkbox-label::after,
&.vds-checkbox:checked + .vds-checkbox-label.vds-state--checked::after {
  content: 'x';
}

& + .vds-checkbox-label:indeterminate::after,
& + .vds-checkbox-label.vds-state--indeterminate::after {
  content: '-';
}
`;

const highContrastIndeterminateStyles = color => `
& + .vds-checkbox-label:indeterminate::after,
& + .vds-checkbox-label.vds-state--indeterminate::after {
  content: '-';
  line-height: 1;
  text-align: center;
  color: ${color};
  color: windowText;
  background-image: none !important;
}
`;

/* 
  "(Note that the SVG content needs to be url-escaped for this to work, e.g. # gets replaced with %23.)"
  -- https://stackoverflow.com/questions/10768451/inline-svg-in-css
  All key value pairs also need the value to be surrounded by single quotes
*/
const toInlineSVGValue = str => {
  const formattedStr = str.includes('#') ? `%23${str.substring(1)}` : str;

  return `${formattedStr}`;
};

const isHighContrast = BrowserUtils.isHighContrastMode();

const stylesBuilder = () => ({
  wrapperStyles: () => css`
    position: relative;
    label {
      word-break:break-word;
    }
  `,
  errorStyles: () => css`
    word-break:break-word;
    &.vds-checkbox-error-message svg.vds-icon {
      min-width: 25px;
    }
  `,
  checkboxStyles: ({ cybs, vds }) => css`
    &.vds-checkbox {
      width: 2rem;
      height: 2rem;
      & + .vds-checkbox-label {
        min-width: 2.5rem;
        min-height: 2.5rem;

        &:empty {
          padding: 0;
          display: block;
        }
      }
    }

    /* Default label color*/
    & + .vds-checkbox-label:not(.vds-state--disabled){
      color: ${vds?.miscExtraDark ?? '#000000'};
    }

    /* Focus state */
    &.vds-checkbox.focus-visible + .vds-checkbox-label.vds-state--focus::before{
      background-color: ${vds?.tableHighlight ?? '#C7EDFF'};
      border: 1px dashed ${cybs?.core?.primaryDark ?? '#0F2595'};
    }

    /* Pressed state */
    & + .vds-checkbox-label:active::before{
      background-color: ${vds?.tableHighlight ?? '#C7EDFF'};
      border: 0.1px solid ${vds?.primaryExtraDark2 ?? '#021E4C'};
    }

    /* Disabled state */
    & + .vds-checkbox-label.vds-state--disabled {
      color: ${cybs?.base?.wcag21?.disabledGrey ?? '#00000033'};
      ::after {
        border-color: ${cybs?.base?.wcag21?.disabledGrey ?? '#00000033'};
      }
    }

    .vds-checkbox-label:not(.vds-state--disabled)::after{
      border: 2px solid ${cybs?.core?.primary ?? '#1434CB'};
    }
    &.vds-checkbox:checked + .vds-checkbox-label.vds-state--disabled::after {
      background-color: ${cybs?.base?.wcag21?.disabledGrey ?? '#00000033'};
      border: none;
    }

    /* Indeterminate state */
    & + .vds-checkbox-label:indeterminate::after,
    & + .vds-checkbox-label.vds-state--indeterminate::after {
      background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' xmlns='http://www.w3.org/2000/svg'%3E%3Crect stroke-width='0' fill='${vds
        ? toInlineSVGValue(vds.radioChecked)
        : '%23003ea9'}' height='10' width='10' ry='2' rx='2' y='3' x='3'/%3E%3C/svg%3E");
    }

    /* Disabled state */
    & + .vds-checkbox-label:indeterminate.vds-state--disabled::after,
    &
      + .vds-checkbox-label.vds-state--indeterminate.vds-state--disabled::after {
      background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' xmlns='http://www.w3.org/2000/svg'%3E%3Crect stroke-width='0' fill='${toInlineSVGValue(
        cybs?.base?.wcag21?.disabledGrey ?? '%00000033'
      )}' height='10' width='10' ry='2' rx='2' y='3' x='3'/%3E%3C/svg%3E");
    }

    ${isHighContrast
      ? highContrastCheckedStyles('white')
      : ''}
  `,
  indeterminateStyles: ({ cybs, vds }) => css`
    & + .vds-checkbox-label:indeterminate::after,
    & + .vds-checkbox-label.vds-state--indeterminate::after {
      background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' xmlns='http://www.w3.org/2000/svg'%3E%3Crect stroke-width='0' fill='${vds
        ? toInlineSVGValue(vds.radioChecked)
        : '%23003ea9'}' height='10' width='10' ry='2' rx='2' y='3' x='3'/%3E%3C/svg%3E");
    }

    /* Disabled state */
    & + .vds-checkbox-label:indeterminate.vds-state--disabled::after,
    &
      + .vds-checkbox-label.vds-state--indeterminate.vds-state--disabled::after {
      background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' xmlns='http://www.w3.org/2000/svg'%3E%3Crect stroke-width='0' fill='${toInlineSVGValue(
        cybs?.base?.cybs?.gray ?? '%23b5b5b5'
      )}' height='10' width='10' ry='2' rx='2' y='3' x='3'/%3E%3C/svg%3E");
    }

    @media screen and (-ms-high-contrast: active) {
      ${highContrastIndeterminateStyles('white')}
    }

    @media all and (-ms-high-contrast: black-on-white) {
      ${highContrastIndeterminateStyles('black')}
    }

    ${BrowserUtils.isHighContrastMode()
      ? highContrastIndeterminateStyles('black')
      : ''}
  `
});

export default stylesBuilder;
